import type { JSX, ReactNode } from 'react'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

/**
 * The top of every screen, in one place.
 *
 * Each page had built its own: some set a weight the theme already sets, some
 * wrapped the actions in a row and some did not, and the gap between the title
 * and the first card was a different number on four of them. None of that is
 * visible on any single screen — it only shows up as the feeling that the
 * product was assembled rather than designed, which is the hardest kind of
 * problem to file a bug against.
 *
 * `actions` sits on the same baseline as the title and wraps below it on a
 * narrow screen rather than squeezing the heading, because a heading that has
 * been squeezed to fit a button reads as broken and a button on its own line
 * does not.
 */
export function PageHeader({
  title,
  description,
  actions,
  meta,
}: Readonly<{
  title: string
  /** One or two sentences. What this screen is for, not what it contains. */
  description?: ReactNode
  /** Primary actions for the screen. Right-aligned, wrapping below on narrow. */
  actions?: ReactNode
  /** A status line under the description — counts, freshness, a warning. */
  meta?: ReactNode
}>): JSX.Element {
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={2}
      alignItems={{ xs: 'stretch', sm: 'flex-start' }}
      sx={{ mb: 0.5 }}
    >
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography variant="h5" component="h1">
          {title}
        </Typography>
        {description ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 860 }}>
            {description}
          </Typography>
        ) : null}
        {meta ? <Box sx={{ mt: 1 }}>{meta}</Box> : null}
      </Box>
      {actions ? (
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0, alignItems: 'center' }}>
          {actions}
        </Stack>
      ) : null}
    </Stack>
  )
}

/**
 * A titled block of a screen.
 *
 * Sections were `<Typography variant="subtitle2">` followed by a Stack on some
 * screens and a Card with a header on others, so the same kind of grouping
 * looked like two different kinds of thing depending which page you were on.
 */
export function SectionHeader({
  title,
  description,
  actions,
}: Readonly<{
  title: string
  description?: ReactNode
  actions?: ReactNode
}>): JSX.Element {
  return (
    <Stack direction="row" alignItems="flex-start" spacing={2} sx={{ mb: 1.5 }}>
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography variant="subtitle1" fontWeight={650}>
          {title}
        </Typography>
        {description ? (
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        ) : null}
      </Box>
      {actions ? <Box sx={{ flexShrink: 0 }}>{actions}</Box> : null}
    </Stack>
  )
}
